// get_leads returns a tab_status per financier on each lead:
//   { sent_to_financier | noa_approval | contract_signing: { status, is_active } }
// Kept as { [financierId]: { send, noa, contract } } keyed like every other
// bank-keyed store (String(financier_id)).
const API_TABS = { send: "sent_to_financier", noa: "noa_approval", contract: "contract_signing" };
const NEXT_STAGE = { send: "noa", noa: "contract" };

// The financier array's key isn't fixed across get_leads versions.
const financierRows = (row) =>
  [row?.financiers, row?.financier_details, row?.financier_list, row?.lead_financiers].find(Array.isArray) || [];

export const toFinancierTabStatus = (row) => {
  const out = {};
  financierRows(row).forEach((f) => {
    const id = f?.financier_id ?? f?.id;
    if (id == null || !f.tab_status) return;
    const entry = {};
    Object.entries(API_TABS).forEach(([stage, apiKey]) => {
      const t = f.tab_status[apiKey];
      if (t) {
        entry[stage] = {
          status: t.status || "",
          isActive: !!t.is_active,
          // Stage chip for the bank tab (In Progress, Deferred, NOA Received…).
          chip: t.stage_status ? { code: t.stage_status, label: t.stage_status_label || t.stage_status } : null,
        };
      }
    });
    out[String(id)] = entry;
  });
  return out;
};

// Per-bank status and allowed actions, keyed like tabStatus:
//   { [financierId]: { status: {code,label}, actions: {canDefer,canResume,canCancel},
//                      defer: {...}, cancel: {...} } }
// Same source shape for a get_leads row and a defer/cancel response snapshot.
export const toBankState = (row) => {
  const out = {};
  financierRows(row).forEach((f) => {
    const id = f?.financier_id ?? f?.id;
    if (id == null || !f.bank_status) return;
    out[String(id)] = {
      status: f.bank_status,
      actions: {
        canDefer: !!f.actions?.can_defer,
        canResume: !!f.actions?.can_resume,
        canCancel: !!f.actions?.can_cancel,
      },
      defer: f.defer_details || null,
      cancel: f.cancel_details || null,
    };
  });
  return out;
};

// Lead-level flags from a get_leads row, a status snapshot or
// get_lead_details.application_status (all carry the same keys).
export const toApplicationState = (src) =>
  src
    ? {
        isLost: !!src.is_lost,
        isCancelled: !!src.is_cancelled,
        isContractSigned: !!src.is_contract_signed,
        isReopened: !!src.is_reopened,
        canCancelApplication: !!src.can_cancel_application,
        cancelNote: src.cancel_note || "",
        cancelDetails: src.cancel_details || null,
      }
    : null;

// "complete" (status approved, or deferred — sent, waiting at the bank) · "active" (is_active — its action is allowed)
// · "locked" (neither — the action can't be taken) · null when get_leads gave
// nothing for this bank/stage (e.g. a bank added on the Send page), in which
// case callers keep their own existing behaviour.
export const stageState = (tabStatus, key, stage) => {
  const t = tabStatus?.[key]?.[stage];
  if (!t) return null;
  if (t.status === "approved" || t.status === "deferred") return "complete";
  return t.isActive ? "active" : "locked";
};

// Local update after a successful save, so the page doesn't wait for a
// dashboard reload: the stage gets `status` and, once approved, the next
// stage becomes active.
export const withStageDone = (tabStatus, key, stage, status) => {
  const cur = { ...(tabStatus?.[key] || {}) };
  cur[stage] = { status, isActive: false };
  const next = NEXT_STAGE[stage];
  if (status === "approved" && next && cur[next]?.status !== "approved") cur[next] = { status: cur[next]?.status || "pending", isActive: true };
  return { ...(tabStatus || {}), [key]: cur };
};

// Banks get_leads gave nothing for (opened straight from the create flow, or
// added on the Send page) start as a fresh bank would on the dashboard: Send
// active, NOA and Contract locked until the stage before them is approved.
export const withDefaultStages = (tabStatus, keys) => {
  const missing = keys.filter((k) => !tabStatus?.[k]);
  if (!missing.length) return tabStatus || {};
  const out = { ...(tabStatus || {}) };
  missing.forEach((k) => {
    out[k] = {
      send: { status: "pending", isActive: true },
      noa: { status: "pending", isActive: false },
      contract: { status: "pending", isActive: false },
    };
  });
  return out;
};
